import type { CrmContact, CrmStageCount } from '../../types/crm';

interface CrmKanbanBoardProps {
  stages: CrmStageCount[];
  contacts: CrmContact[];
  onOpenContact: (contact: CrmContact) => void;
  onMoveContact: (contactId: string, stage: string) => void;
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Sem data';
  return new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit'
  });
};

export function CrmKanbanBoard({ stages, contacts, onOpenContact, onMoveContact }: CrmKanbanBoardProps) {
  const grouped = stages.map((stage) => ({
    ...stage,
    contacts: contacts.filter((contact) => contact.statusJornada === stage.key)
  }));

  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex min-w-max gap-4">
        {grouped.map((stage) => (
          <div
            key={stage.key}
            className="w-[320px] rounded-3xl border border-slate-200 bg-slate-50/80 p-4"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const contactId = event.dataTransfer.getData('text/plain');
              if (contactId) {
                onMoveContact(contactId, stage.key);
              }
            }}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">{stage.label}</p>
                <p className="text-xs text-slate-500">{stage.contacts.length} contatos</p>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                {stage.count}
              </span>
            </div>

            <div className="space-y-3">
              {stage.contacts.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/plain', contact.id);
                  }}
                  onClick={() => onOpenContact(contact)}
                  className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{contact.nome}</p>
                      <p className="text-xs text-slate-500">{contact.telefone}</p>
                      <p className="text-xs text-slate-500">{contact.email || 'Sem e-mail'}</p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600">
                      {contact.nivelInteresse || 'Neutro'}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-slate-400">Interesse</span>
                      <span>{contact.interestLabel || contact.interessePrincipal || 'Nao definido'}</span>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-slate-400">Responsavel</span>
                      <span>{contact.responsavel || 'Nao definido'}</span>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-slate-400">Ultimo contato</span>
                      <span>{formatDate(contact.ultimoContato || contact.lastInteraction?.criadoEm)}</span>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-slate-400">Proxima acao</span>
                      <span>{formatDate(contact.proximaAcao || contact.nextTask?.dataAgendada)}</span>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1">
                    {(contact.tagItems || []).slice(0, 3).map((tag) => (
                      <span
                        key={tag.id}
                        className="rounded-full px-2 py-1 text-[11px] font-medium text-white"
                        style={{ backgroundColor: tag.color }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </button>
              ))}

              {stage.contacts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white/70 p-5 text-center text-sm text-slate-400">
                  Nenhum contato nesta etapa.
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
